import type { FC } from "react";
import { useEffect, useRef } from "react";

import { useTranslations } from "@ui/providers/useTranslations";
import { displayToastError } from "@ui/utils/displayToastError";

import { newrelic } from "@jti/fe-clients";
import { isServerSide } from "@jti/fe-common";
import { CasCustomAttrsApi, getUser, LoyaltyApi } from "@jti/rest-api";
import type { IUser } from "@jti/rest-api-types";

export type SurveyProps = {
  scriptUrl: string;
};

declare global {
  interface Window {
    QSI?: {
      config?: {
        externalReference: string;
        ploomPoints?: number;
        availableComplaints: unknown;
      };
      API?: {
        unload?: () => void;
      };
    };
  }
}

export const Survey: FC<SurveyProps> = (props) => {
  const { dictionary } = useTranslations();
  const { scriptUrl } = props;

  const injectedScriptRef = useRef<HTMLScriptElement | null>(null);
  const isMountedRef = useRef(true);

  const getScriptContent = async () => {
    const scriptContent = await fetch(scriptUrl);
    return scriptContent.text();
  };

  const getPointsAndComplaints = async (user: IUser) => {
    try {
      const casCustomAttrsApi = CasCustomAttrsApi.init();
      const loyaltyApi = LoyaltyApi.init();

      const [isUserHasComplaints, pointsHistory, scriptContent] = await Promise.all([
        casCustomAttrsApi.getIsUserHasComplaints(),
        loyaltyApi.getPointsHistory(user.uuid ?? "", user.ciam_token ?? ""),
        getScriptContent(),
      ]);

      if (isUserHasComplaints) {
        return;
      }

      if (!window.QSI) {
        window.QSI = {};
      }
      window.QSI.config = {
        externalReference: user.uuid ?? "",
        availableComplaints: isUserHasComplaints,
        ploomPoints: pointsHistory?.items.reduce((accumulator, item) => accumulator + item.points, 0),
      };

      const parser = new DOMParser();
      const code = parser.parseFromString(scriptContent, "text/html");
      if (isServerSide()) {
        newrelic.noticeError(new Error("document is not available"));
        return;
      }

      if (!isMountedRef.current) {
        return;
      }
      const scriptNode = document.createElement("script");
      scriptNode.innerHTML = code.getElementsByTagName("script")[0].text;
      document.head.appendChild(scriptNode);
      injectedScriptRef.current = scriptNode;
    } catch (e) {
      console.log("getPointsAndComplaints Error ==>", e);

      displayToastError(e, undefined, dictionary);
    }
  };

  const teardownSurvey = () => {
    injectedScriptRef.current?.remove();
    injectedScriptRef.current = null;

    if (isServerSide()) {
      return;
    }

    try {
      window.QSI?.API?.unload?.();
    } catch {
      // unload() is best-effort; ignore Qualtrics engine errors.
    }

    // unload() leaves its creative nodes in the DOM; remove the documented leftovers.
    document.querySelectorAll('[class*="QSIWebResponsive"]').forEach((node) => node.remove());
  };

  useEffect(() => {
    isMountedRef.current = true;
    const user = getUser();

    if (user?.ciam_token && user?.uuid) {
      getPointsAndComplaints(user);
    }

    return () => {
      isMountedRef.current = false;
      teardownSurvey();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
